// src/hooks/useWeatherVoice.js
//
// Ticket 408 (#408) — data/lifecycle layer for the homepage Weather Voice
// integration. Owns: today's-row resolution (Atlantic/Reykjavik), the
// Phase 1 engine call, the stable per-mount history adapter, episode-keyed
// client-side selection (Phase 2), and explicit visible-exposure
// recording. Never fetches or normalizes forecast data itself — reads
// only what useForecast.js already produced — and never renders anything
// (src/components/WeatherVoice.jsx is the pure renderer).
//
// Ticket 409 (#409) — a single `weather_voice_viewed` analytics event is
// emitted from inside the SAME validated exposure boundary as history
// recording (see `onVisible` below), never a second observer or a raw/
// intermediate trigger. Payload is a minimal derived subset of episode
// identity (voice_id/language/severity/weather_type/surface) — no free
// text, coordinates, user identity, raw forecast, full episode key, or
// timestamps. No `weather_voice_interacted` event exists: every one of
// every current IS/EN comment has `ctaType: null`, so there is no authored
// interaction to measure (see docs/analytics/weather-voice-production-validation.md).
//
// Ticket 432 (#432) — every active outcome carries a voiceLevel. Sarcastic
// episodes use the joke pool and its cooldown history. Cautious and serious
// episodes use the deterministic safety library and never touch that
// history, and they never produce a share snapshot.
//
// Ticket 410 (#410) — this hook also computes `shareSnapshot`: a narrow,
// frozen snapshot of the actually-displayed episode for the secondary
// "share" action, gated by a conservative editorial promotion policy
// (weatherVoiceSharePolicy.js) — never a safety classifier. See
// src/components/WeatherVoiceShareDialog.jsx for the sharing UI/lifecycle
// and docs/analytics/weather-voice-share-pilot.md for the event contract.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { evaluateWeatherVoice } from "../lib/weatherVoiceEngine";
import { getWeatherVoiceLibrary, devWarnEmptyEligiblePool } from "../lib/weatherVoiceContent";
import { selectWeatherVoicePresentation } from "../lib/weatherVoiceSelector";
import { createWeatherVoiceHistory } from "../lib/weatherVoiceHistory";
import { resolveWeatherVoiceCta } from "../lib/weatherVoicePresentation";
import { trackEvent } from "../lib/analytics";
import { selectWeatherVoiceSupplement, validateWeatherVoiceSupplements } from "../lib/weatherVoiceSupplement";
import { createWeatherVoiceSupplementHistory } from "../lib/weatherVoiceSupplementHistory";
import { weatherVoiceSupplements } from "../i18n/weatherVoice/supplement";
import { buildWeatherVoiceShareSnapshot } from "../lib/weatherVoiceShareSnapshot";

const SURFACE_HOMEPAGE_DECISION = "homepage_decision";
const MIDNIGHT_POLL_MS = 60000;

/**
 * getReykjavikDateString(epochMs) -> "YYYY-MM-DD"
 * The calendar date in Atlantic/Reykjavik for the given instant — never
 * the browser's local timezone or UTC-by-assumption.
 * @param {number} epochMs
 * @returns {string}
 */
export function getReykjavikDateString(epochMs) {
  // "en-CA" formats as YYYY-MM-DD directly, matching normalized rows'
  // `date` field shape exactly — no separate parsing/reformatting needed.
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Atlantic/Reykjavik" }).format(new Date(epochMs));
}

/**
 * findTodayRow(rows, dateString) -> row | null
 * The exact matching row for `dateString`, or null. Never rows[0], never
 * a substitute (e.g. tomorrow) when today's row is missing.
 * @param {Array<Object>} rows
 * @param {string} dateString
 * @returns {Object | null}
 */
export function findTodayRow(rows, dateString) {
  if (!Array.isArray(rows) || typeof dateString !== "string" || dateString.length === 0) return null;
  return rows.find((r) => r?.date === dateString) ?? null;
}

/**
 * buildWeatherVoiceEpisodeKey({ surface, siteId, dateString, lang, engineResult })
 * -> string | null
 * Stable string key identifying one "episode": the same site, day,
 * language, and Phase 1 outcome. Built from primitive VALUES only (never
 * object identity), so object-identity-only row/result rebuilds with the
 * same underlying values never change the key. `null` whenever there's no
 * genuinely active engine result to key.
 */
export function buildWeatherVoiceEpisodeKey({ surface, siteId, dateString, lang, engineResult }) {
  if (!engineResult?.show) return null;
  if (siteId == null || !dateString || !lang) return null;
  return [surface, siteId, dateString, lang, engineResult.condition, engineResult.mood, engineResult.severity].join("|");
}

/**
 * useWeatherVoice(args) -> { presentation, action, episodeKey, onVisible, shareSnapshot }
 *
 * `episodeKey` identifies the current episode (site/date/language/Phase-1-
 * outcome) and must be passed through to WeatherVoiceCard's `episodeKey`
 * prop — it's what lets a real exposure observation be bound to the
 * specific episode it was actually watching, not just "whatever happens
 * to be current when the callback fires" (see `onVisible` below).
 *
 * @param {{
 *   enabled: boolean,
 *   site: { id: string|number, lat: number, lon: number } | null,
 *   rows: Array<Object>,
 *   requestedFor: { lat: number, lon: number } | null,
 *   loading: boolean,
 *   error: unknown,
 *   lang: string,
 *   t: Function,
 *   onExplore?: Function,
 *   now?: () => number,
 *   rng?: () => number,
 *   storage?: { getItem: Function, setItem: Function },
 * }} args
 */
export function useWeatherVoice({
  enabled = false,
  site = null,
  rows = [],
  requestedFor = null,
  loading = false,
  error = null,
  lang,
  t,
  onExplore,
  now = Date.now,
  rng = Math.random,
  storage,
  supplementRegistry = weatherVoiceSupplements,
} = {}) {
  // Stable for the mounted session — never recreated on rerender, never
  // touches localStorage at creation (createWeatherVoiceHistory only
  // touches storage when its own methods are called).
  const historyRef = useRef(null);
  if (!historyRef.current) {
    historyRef.current = createWeatherVoiceHistory(storage ? { storage } : undefined);
  }

  // Supplement failures must never reach the primary warning (#420 R1).
  // Registry iteration is guarded here as well, because it runs at mount.
  const supplementHistoryRef = useRef(null);
  if (!supplementHistoryRef.current) {
    let knownIds = [];
    try {
      knownIds = validateWeatherVoiceSupplements(supplementRegistry).valid.map((e) => e.id);
    } catch {
      knownIds = [];
    }
    supplementHistoryRef.current = createWeatherVoiceSupplementHistory({ storage: storage ?? undefined, knownIds });
  }

  const [todayDate, setTodayDate] = useState(() => getReykjavikDateString(now()));

  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => {
      setTodayDate((prev) => {
        const next = getReykjavikDateString(now());
        return prev === next ? prev : next;
      });
    }, MIDNIGHT_POLL_MS);
    return () => clearInterval(id);
  }, [enabled, now]);

  const siteReady = enabled && site?.id != null && Number.isFinite(site?.lat) && Number.isFinite(site?.lon);
  const provenanceMatches = siteReady && requestedFor != null && requestedFor.lat === site.lat && requestedFor.lon === site.lon;
  const dataReady = siteReady && provenanceMatches && !loading && !error;

  const todayRow = useMemo(() => (dataReady ? findTodayRow(rows, todayDate) : null), [dataReady, rows, todayDate]);

  const engineResult = useMemo(() => {
    if (!todayRow) return { show: false };
    return evaluateWeatherVoice({ tmax: todayRow.tmax, windMax: todayRow.windMax, rain: todayRow.rain, code: todayRow.code });
  }, [todayRow]);

  const episodeKey = useMemo(
    () => buildWeatherVoiceEpisodeKey({ surface: SURFACE_HOMEPAGE_DECISION, siteId: site?.id, dateString: todayDate, lang, engineResult }),
    [site?.id, todayDate, lang, engineResult]
  );

  // Selection happens here (an effect — never render/useMemo), guarded so
  // a given episodeKey is only ever selected ONCE per mount, even across
  // React StrictMode's synthetic double-invoke of this same effect (the
  // ref survives that replay; the RNG/history call inside the guard does
  // not run twice).
  const selectionCacheRef = useRef({ key: null, presentation: { show: false } });
  const supplementCacheRef = useRef({ key: null, supplement: { show: false } });
  const [resolvedKey, setResolvedKey] = useState(null);

  useEffect(() => {
    if (!episodeKey) return;
    if (selectionCacheRef.current.key !== episodeKey) {
      const library = getWeatherVoiceLibrary(lang);
      const history = historyRef.current.getHistory(now());
      const selected = selectWeatherVoicePresentation({ engineResult, lang, library, history, now: now(), rng });
      // Ticket 412 (#412): the engine wanted to show something, but this
      // language's resolved library had zero eligible entries for that
      // exact condition/mood — the approved prompt's "exceptional"
      // wholly-absent-pool case. selectWeatherVoiceComment already fails
      // closed (show:false) on its own; this only adds a bounded,
      // dev-only diagnostic on top of that already-conservative behavior
      // — the selector itself stays pure, untouched.
      if (engineResult.show && !selected.show) {
        devWarnEmptyEligiblePool(lang, engineResult.condition, engineResult.mood);
      }
      selectionCacheRef.current = { key: episodeKey, presentation: selected };

      let supplement = { show: false };
      try {
        supplement = selectWeatherVoiceSupplement({
          engineResult,
          presentation: selected,
          lang,
          registry: supplementRegistry,
          history: supplementHistoryRef.current.getHistory(now()),
          now: now(),
          rng,
        });
      } catch {
        supplement = { show: false };
      }
      supplementCacheRef.current = { key: episodeKey, supplement };
    }
    setResolvedKey(episodeKey);
  }, [episodeKey, lang, engineResult, now, rng, supplementRegistry]);

  // Synchronous, render-time derivation — never the lagging `resolvedKey`
  // state alone. The instant episodeKey changes (site/date/language/
  // outcome change, or data becomes invalid/loading), this expression
  // evaluates to {show:false} on THIS SAME render, before the selection
  // effect above has even run — so even the very first render after a
  // change cannot show (or later record) a stale episode's content.
  const presentation = episodeKey && resolvedKey === episodeKey ? selectionCacheRef.current.presentation : { show: false };
  const supplement =
    episodeKey && resolvedKey === episodeKey && supplementCacheRef.current.key === episodeKey
      ? supplementCacheRef.current.supplement
      : { show: false };

  // Exposure recording reads everything through refs (always the LATEST
  // value at call time), so `onVisible` itself can stay a stable callback
  // reference and a late/queued visibility callback can never record a
  // stale episode that has since changed.
  const recordedEpisodesRef = useRef(new Set());
  const episodeKeyRef = useRef(episodeKey);
  const resolvedKeyRef = useRef(resolvedKey);
  const presentationRef = useRef(presentation);
  const langRef = useRef(lang);
  episodeKeyRef.current = episodeKey;
  resolvedKeyRef.current = resolvedKey;
  presentationRef.current = presentation;
  langRef.current = lang;

  // Revision 3 (#408, Ripley Revision 2 REVISE) — `onVisible` now REQUIRES
  // the caller (WeatherVoiceCard's observer) to report WHICH episode it
  // actually observed, captured at the observer's own creation time. A
  // bare "something became visible" signal is not evidence that TODAY's
  // episode was seen — a still-connected observer that never restarted
  // (e.g. because two different site/day episodes happened to select the
  // same comment id) can go on firing with a stale captured identity even
  // though it's still a live, non-stale connection. Comparing the
  // reported identity against the CURRENT episode (via the same
  // always-latest refs as before) rejects that case explicitly, not by
  // coincidence of value equality.
  // Ticket 409 (#409): analytics is added at this SAME already-validated
  // boundary — never a second observer, never from render/selection/asset
  // load. Every rejection check above (mismatched/stale key, mid-selection,
  // non-show, already-recorded) gates the analytics call exactly as it
  // already gates history, so "recorded in history" and "counted as a
  // weather_voice_viewed impression" always correspond to the same real
  // observation. `recordedEpisodesRef.add(key)` happens BEFORE the
  // analytics call, so a thrown analytics helper can never cause a retry/
  // duplicate emission for this episode — it only ever loses that one
  // impression, isolated from history (which already recorded) and from
  // rendering (unaffected either way).
  const onVisible = useCallback(
    (observedEpisodeKey) => {
      const key = episodeKeyRef.current;
      if (!key) return; // suppressed/invalid/hidden episode
      if (observedEpisodeKey !== key) return; // stale/mismatched episode identity
      if (resolvedKeyRef.current !== key) return; // mid-selection or stale
      const current = presentationRef.current;
      if (!current?.show) return; // selection landed on content-unavailable silence
      if (recordedEpisodesRef.current.has(key)) return; // already recorded this episode this mount
      recordedEpisodesRef.current.add(key);
      // #432 — safety text never enters the joke cooldown history.
      if (current.voiceLevel === "sarcastic") historyRef.current.recordShown(current, now());
      try {
        trackEvent("weather_voice_viewed", {
          voice_id: current.comment.id,
          language: langRef.current,
          severity: current.severity,
          weather_type: current.condition,
          surface: SURFACE_HOMEPAGE_DECISION,
          voice_level: current.voiceLevel,
        });
      } catch {
        // Isolated: an analytics helper failure must never break visibility
        // or history handling, which have already completed above.
      }
    },
    [now]
  );

  // Separate exposure boundary for the heavy-rain supplement (#420 R5). Only a
  // real visible supplement for the current episode is recorded, and it goes to
  // its own history key and its own event, never to the primary viewed event.
  const recordedSupplementRef = useRef(new Set());
  const onSupplementVisible = useCallback(
    (observedEpisodeKey, supplementId) => {
      try {
        const key = episodeKeyRef.current;
        if (!key || observedEpisodeKey !== key) return;
        if (resolvedKeyRef.current !== key) return;
        const current = presentationRef.current;
        if (!current?.show || current.voiceLevel !== "cautious" || current.condition !== "heavy_rain") return;
        if (current.comment?.id !== "safety_heavy_rain") return;
        const sup = supplementCacheRef.current.key === key ? supplementCacheRef.current.supplement : null;
        if (!sup?.show || sup.supplementId !== supplementId) return;
        const dedupeKey = `${key}|${supplementId}`;
        if (recordedSupplementRef.current.has(dedupeKey)) return;
        recordedSupplementRef.current.add(dedupeKey);
        supplementHistoryRef.current.recordShown(supplementId, now());
        trackEvent("weather_voice_supplement_viewed", {
          supplement_id: supplementId,
          parent_voice_id: sup.parentVoiceId,
          language: langRef.current,
          weather_type: sup.condition,
          surface: SURFACE_HOMEPAGE_DECISION,
        });
      } catch {
        // Isolated: supplement exposure must never affect the primary warning.
      }
    },
    [now]
  );

  const action = useMemo(
    () => resolveWeatherVoiceCta({ ctaType: presentation.show ? presentation.ctaType : null, t, onExplore }),
    [presentation.show, presentation.ctaType, t, onExplore]
  );

  // Ticket 410 (#410) — narrow share-context adapter, computed at this
  // SAME hook/integration boundary from the exact triple (presentation,
  // todayRow, site) already backing the rendered card — never an
  // independent fetch, never a newly-selected comment, never mixed with a
  // different site/day's data. `null` whenever the episode is not
  // share-eligible (conservative good/excellent-only policy, invalid/
  // missing data, unsupported language, or an available hazard veto — see
  // weatherVoiceSharePolicy.js) or the display hasn't settled on this
  // episode yet.
  const shareSnapshot = useMemo(
    () =>
      episodeKey && resolvedKey === episodeKey
        ? buildWeatherVoiceShareSnapshot({ presentation, lang, site, todayRow, todayDate, episodeKey })
        : null,
    // Depends on presentation's own relevant fields, not the object
    // reference itself — `presentation` is a fresh `{ show: false }`
    // literal on every render along the "not resolved yet" branch above,
    // which would otherwise make this memo recompute every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [episodeKey, resolvedKey, presentation.show, presentation.condition, presentation.mood, presentation.severity, presentation.comment?.id, presentation.comment?.text, lang, site, todayRow, todayDate]
  );

  // Revision 2 (#408, Jonesy Round 1 BLOCKED) — a nine-condition
  // "supportingText" auto-hookup was built here in an earlier pass without
  // review; it's removed. WeatherVoiceCard.jsx still accepts an optional
  // `supportingText` prop (the seam stays available for a future,
  // deliberately-reviewed content source), but this hook does not supply
  // one — production support is genuinely absent until that content is
  // authored and approved on its own terms.

  return { presentation, action, episodeKey, onVisible, shareSnapshot, supplement, onSupplementVisible };
}
